import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';
import { VALUE_ESCAPE } from 'keycode-js';
import PerfectScrollbar from 'react-perfect-scrollbar';

// Actions
import { addSearchItemLog } from '@/redux/modules/activity-log';
import { fetchQuickSearchResults, QUICK_SEARCH_REDUCER_NAME } from '@/redux/modules/quick-search';
import { loadSearchFilters } from '@/redux/modules/advanced-search';
import { selectQuickSearchShelfStatusedResults, selectSavedFundsShelfStatusedFund } from '@/redux/modules/shelf-management/selectors';
import { setHoldingToReplace } from '@/redux/modules/edit-portfolio';
import { getCustomProxies } from '@/redux/modules/custom-proxies';

// Components
import CollapsibleContainer from '@/views/components/misc/CollapsibleContainer';
import LoadingSpinner from '@/views/components/common/LoadingSpinner';
import QuickSearchV2Item, { QUICK_SEARCH_V2_ITEM } from './QuickSearchV2Item';
import { SecondaryLink } from '@/views/components/buttons';
import { ControlItem, Tabs } from '@/views/components/navigation';
import { SearchInput } from '@/views/components/inputs';
import { SimulatorModal } from '@/views/components/modals';
import RateOfReturnModal from '@/views/components/modals/RateOfReturnModal';
import { Tooltip } from '@/views/components/tooltips';
import { withErrorBoundary } from '@/views/components/errors';

// Enums
import { COMPARISON_ITEM_TYPES, YieldParamsType } from '@/utils/constants';
import { CUSTOM_PROXY_TYPE } from '@/enums/CustomProxy';
import { INSTRUMENT_TYPE } from '@/enums/InstrumentByCode';
import { QUICK_SEARCH_FILTERS } from '@/enums/QuickSearch';

// Types
import Comparison from '@/types/comparison';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import ComparisonItemType from '@/types/comparison-item-type';
import Fund from '@/types/fund';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import PortfolioHoldingType from '@/types/portfolio-holding-type';
import QuickAddGICSimulator from '@/types/quick-add-gic-simulator';
import QuickAddHISASimulator from '@/types/quick-add-hisa-simulator';
import QuickAddItems from '@/types/quick-add-items';
import QuickAddItemsList from '@/types/quick-add-items-list';
import QuickAddUIType from '@/types/quick-add-u-i-type';
import QuickSearchItem from '@/types/quick-search-item';
import RecentSearchItem from '@/types/recent-search-item';
import User from '@/types/user';
import { FundSearchState } from '@/redux/modules/types/fund-search';
import { ReduxState } from '@/redux/modules/types';
import CUSTOM_PROXY_ITEM from '@/types/custom-proxy-item';

// Utils
import exchangeMapper from '@/utils/exchange-mapper';
import helpers from '@/utils/disclaimers/helpers';
import { benchmarkToQuickSearchItem, filterIsShown, fundToQuickSearchItem, getPillLabel } from '@/utils/quick-search';
import { deepClone } from '@/utils/objects';
import { formatLocalizedString, formatMessage, FormattedMessage, LOCALE } from '@/utils/i18n';
import { showError } from '@/utils/errors';
import { searchObjectsOnFields } from '@/utils/search';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';
import { processCustomProxiesData, getCustomProxiesCount } from '@/utils/custom-proxies';
import { SEARCH_ITEM_DEST, SEARCH_ITEM_SOURCE } from '@/utils/activity-log';

// Legacy support
import LegacyQuickSearchV2 from '@/views/components/search/quick-search-v2/LegacyQuickSearchV2';
import withLegacyVersion from '@/views/components/hocs/withLegacyVersion';
import { CustomProxiesData } from '@/types/custom-proxies-state';
import { getShelfNameFromShelvableInstrument } from '@/utils/user-shelf';

type ALL_SECTION = {
  i18nKey: string;
  value: string;
  getOptions: () => QUICK_SEARCH_V2_ITEM[];
};

enum QUICK_SEARCH_ALL_SECTIONS {
  QUICK_ADD = 'quickAdd',
  RECENT_SEARCH = 'recentSearch',
  SIMULATION = 'simulation',
}

type DispatchProps = {
  addSearchItemLog: (id: string, type: string, source: string, dest: string) => void;
  fetchQuickSearchResults: (
    query: string,
    reducerName: string,
    types?: QUICK_SEARCH_FILTERS[],
    excludeSubtypes?: boolean,
    limit?: number,
    universe?: string,
  ) => Promise<void>;
  loadSearchFilters: (overrideLocale: Locale) => Promise<void>;
  setHoldingToReplace: (holdingToReplace?: PortfolioHolding) => void;
  getCustomProxies: () => Promise<CustomProxiesData>;
  disabled?: boolean;
};

type StateProps = {
  fundState: FundSearchState;
  isFocusedQuickSearch?: boolean;
  enableOnlyProxies?: boolean;
  myFunds: Fund[];
  myBenchmarks: ComparisonBenchmark[];
  results: QuickSearchItem[];
  user?: Nullable<User>;
  customProxies?: CustomProxiesData;
};

type LocalProps = {
  targetReducer: QUICK_SEARCH_REDUCER_NAME;
};

type OptionsProps = {
  withAdvancedScreener?: boolean;
  hiddenTabs?: QUICK_SEARCH_FILTERS[];
  allowedTypes?: QUICK_SEARCH_FILTERS[];
  excludeSubtypes?: boolean;
};

type Props = LocalProps &
  DispatchProps &
  StateProps & {
    comparison?: Nullable<Comparison>;
    holdingToReplace?: PortfolioHolding;
    onAddAltById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
    onAddBenchmarkById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
    onAddFundById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
    onAddStockById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
    onOpenScreenerModal: (open: boolean) => void;
    onSetFocusedItem: (focused?: boolean) => void;
    options?: OptionsProps;
    portfolio?: Portfolio;
    quickAddItems?: QuickAddItems[];
    recentSearchItems?: RecentSearchItem[];
  };

const QuickSearchV2 = ({
  addSearchItemLog,
  comparison,
  fetchQuickSearchResults,
  fundState: { filtersData: fundFilters },
  holdingToReplace,
  isFocusedQuickSearch,
  enableOnlyProxies,
  loadSearchFilters,
  myBenchmarks,
  myFunds,
  onAddAltById,
  onAddBenchmarkById,
  onAddFundById,
  onAddStockById,
  onOpenScreenerModal,
  onSetFocusedItem,
  options: { withAdvancedScreener = true, hiddenTabs = [], allowedTypes = undefined, excludeSubtypes = false } = {},
  portfolio,
  quickAddItems,
  results,
  setHoldingToReplace,
  targetReducer,
  user,
  customProxies,
  getCustomProxies,
  disabled = false,
}: Props) => {
  const itemWrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<SearchInput>(null);
  const componentId = useRef(uniqueId('quick-search-v2-')).current;
  const ignoreNextBlurRef = useRef(false);

  const [addingItemsIds, setAddingItemsIds] = useState<Array<string>>([]);
  const [favoriteItems, setFavoriteItems] = useState<QuickSearchItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [proxyItems, setProxyItems] = useState<QUICK_SEARCH_V2_ITEM[] | Record<string, QUICK_SEARCH_V2_ITEM[]>>([]);
  const [isProxyDataGrouped, setIsProxyDataGrouped] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<QUICK_SEARCH_FILTERS>(QUICK_SEARCH_FILTERS.ALL);
  const [showGicModal, setShowGicModal] = useState<boolean>(false);
  const [showHisaModal, setShowHisaModal] = useState<boolean>(false);
  const [showRateOfReturnModal, setShowRateOfReturnModal] = useState<boolean>(false);
  const [proxySimulationItem, setProxySimulationItem] = useState<CUSTOM_PROXY_ITEM | null>(null);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [simulationItems, setSimulationItems] = useState<QUICK_SEARCH_V2_ITEM[]>([]);

  const isComparisonReducer = targetReducer === QUICK_SEARCH_REDUCER_NAME.COMPARISON;
  const isEditBenchmarkReducer = targetReducer === QUICK_SEARCH_REDUCER_NAME.EDIT_BENCHMARK;
  const isPortfolioReducer = targetReducer === QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO;
  const i18nBasePath = 'Search.QuickSearchV2';

  const getI18nKeyDestination = (): string => {
    if (isComparisonReducer) {
      return 'Comparison';
    }
    if (isEditBenchmarkReducer) {
      return 'Benchmark';
    }
    return 'Portfolio';
  };

  const i18nKeyDestination = getI18nKeyDestination();
  const addItemDefaultLabel = formatMessage(`${i18nBasePath}.AddTo${i18nKeyDestination}`);
  const benchmarkOption = isComparisonReducer ? QUICK_SEARCH_FILTERS.BENCHMARKS : QUICK_SEARCH_FILTERS.BENCHMARK_PROXIES;
  const defaultSearchableOptions = [
    QUICK_SEARCH_FILTERS.FUNDS,
    QUICK_SEARCH_FILTERS.STOCKS,
    QUICK_SEARCH_FILTERS.BONDS,
    QUICK_SEARCH_FILTERS.ALTS,
    benchmarkOption,
  ];
  const allSearchableOptions = allowedTypes || defaultSearchableOptions;
  const debounceRate = 300;
  const maxQueryLimit = 100;
  const queryLimit = 25;
  const resultsLimit = 50;

  const updateProxyItems = useCallback((items: QUICK_SEARCH_V2_ITEM[] | Record<string, QUICK_SEARCH_V2_ITEM[]>, isGrouped: boolean) => {
    setProxyItems(items);
    setIsProxyDataGrouped(isGrouped);
  }, []);

  const fetchSearchResults = (filter: Nullable<QUICK_SEARCH_FILTERS>, query: string): Promise<void> => {
    const isSMAFilter = filter === QUICK_SEARCH_FILTERS.SMA;
    const isSpecificFilter = filter && filter !== QUICK_SEARCH_FILTERS.ALL && !isSMAFilter;
    const limit = isSpecificFilter ? queryLimit : maxQueryLimit;
    const types = isSpecificFilter ? [filter] : allSearchableOptions;
    const universe = isSMAFilter ? QUICK_SEARCH_FILTERS.SMA : undefined;

    return fetchQuickSearchResults(query, targetReducer, types, excludeSubtypes, limit, universe);
  };

  const debouncedFetchQuickSearchResults = useRef(
    debounce((filter: Nullable<QUICK_SEARCH_FILTERS>, query: string) => {
      Promise.resolve(fetchSearchResults(filter, query)).finally(() => setIsLoading(false));
    }, debounceRate),
  ).current;

  // Load the fund filters to display the good filter tabs
  useEffect(() => {
    // Note : we force the locale to be en_CA because we only need to know which filters are available
    // for this user and it is easier to compare when we fix the locale.
    // At the moment it is perfect because we do not need to display them.
    // If you need to display the filters, hence need to get them in the user's language,
    // you must make sure that the function filterIsShown still works properly.
    loadSearchFilters(LOCALE.EN_CA);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = ({ target }: MouseEvent) => {
      if (itemWrapperRef.current && !itemWrapperRef.current.contains(target as Node)) {
        setShowResults(false);
        clearSearch();
        setSelectedFilter(QUICK_SEARCH_FILTERS.ALL);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFocusedQuickSearch) {
      searchInputRef.current?.focusInput();
    }
  }, [isFocusedQuickSearch]);

  useEffect(() => {
    if (userHasPermissionEnabled(user, PERMISSIONS.CUSTOM_PROXIES)) {
      // Only auto-update if:
      // 1. We're not on the proxies filter, OR
      // 2. We're on proxies filter but have no search term to avoid overwriting filtered results during race conditions
      const isOnProxiesFilter = selectedFilter === QUICK_SEARCH_FILTERS.PROXIES;
      const hasSearchTerm = !!searchTerm;

      if (!isOnProxiesFilter || (isOnProxiesFilter && !hasSearchTerm)) {
        const { items, isGrouped } = processCustomProxiesData(customProxies);
        updateProxyItems(items, isGrouped);
      }
    } else {
      updateProxyItems([], false);
    }
  }, [customProxies, user, selectedFilter, searchTerm, updateProxyItems]);

  useEffect(() => {
    if (enableOnlyProxies) {
      setSelectedFilter(QUICK_SEARCH_FILTERS.PROXIES);
    }
  }, [enableOnlyProxies]);

  const clearSearch = (focusInput?: boolean) => {
    if (focusInput) {
      searchInputRef.current?.focusInput();
    }

    if (selectedFilter === QUICK_SEARCH_FILTERS.FAVORITE) {
      setFavoriteItems(getFavoriteItems());
    }

    if (selectedFilter === QUICK_SEARCH_FILTERS.PROXIES) {
      const { items, isGrouped } = getProxyItems();
      updateProxyItems(items, isGrouped);
    }

    if (selectedFilter === QUICK_SEARCH_FILTERS.SIMULATION) {
      setSimulationItems(getSimulationItems());
    }

    return setSearchTerm('');
  };

  const closeResults = () => {
    setSearchTerm('');
    setSelectedFilter(QUICK_SEARCH_FILTERS.ALL);
    setShowResults(false);
    setAddingItemsIds([]);
  };

  const getAllSections = () => [
    {
      i18nKey: `${i18nBasePath}.AllSections.RecentSearch`,
      value: QUICK_SEARCH_ALL_SECTIONS.RECENT_SEARCH,
      getOptions: () => setRecentSearchOptions(),
    },
    {
      i18nKey: `${i18nBasePath}.AllSections.QuickAdd`,
      value: QUICK_SEARCH_ALL_SECTIONS.QUICK_ADD,
      getOptions: () => setQuickAddOptions(),
    },
    {
      i18nKey: `${i18nBasePath}.AllSections.Simulation`,
      value: QUICK_SEARCH_ALL_SECTIONS.SIMULATION,
      getOptions: () => getSimulationItems(),
    },
  ];

  const getFavoriteItems = () => {
    const benchmarks = isComparisonReducer ? myBenchmarks.map(b => benchmarkToQuickSearchItem(b)) : [];
    const funds = myFunds.map(f => fundToQuickSearchItem(f));
    return [...benchmarks, ...funds];
  };

  const filterGroupedProxyItems = useCallback(
    (groupedItems: Record<string, QUICK_SEARCH_V2_ITEM[]>, query: string, isFuzzy: boolean): Record<string, QUICK_SEARCH_V2_ITEM[]> =>
      Object.entries(groupedItems).reduce(
        (acc, [key, items]) => {
          const filtered = searchObjectsOnFields(query, items, ['name'], isFuzzy);
          if (filtered.length > 0) acc[key] = filtered;
          return acc;
        },
        {} as Record<string, QUICK_SEARCH_V2_ITEM[]>,
      ),
    [],
  );

  const getProxyItems = useCallback(() => {
    // Fetch data if we don't have any custom proxies data
    if (!customProxies || customProxies.length === 0) {
      getCustomProxies();
      return { items: proxyItems, isGrouped: false }; // Return current state while fetch is in progress
    }

    return processCustomProxiesData(customProxies);
  }, [customProxies, proxyItems, getCustomProxies]);

  const getSearchPlaceholder = () => {
    if (isEditBenchmarkReducer) {
      return formatMessage('QuickSearch.Placeholder.AllShort');
    }
    switch (selectedFilter) {
      case QUICK_SEARCH_FILTERS.FAVORITE:
        return formatMessage(`${i18nBasePath}.SearchFavorites`);
      case QUICK_SEARCH_FILTERS.PROXIES:
        return formatMessage(`${i18nBasePath}.SearchProxies`);
      case QUICK_SEARCH_FILTERS.SIMULATION:
        return formatMessage(`${i18nBasePath}.SearchSimulations`);
      default:
        return formatMessage(`${i18nBasePath}.SearchHoldings`);
    }
  };

  const getSimulationItems = () => {
    const simulationItems: QUICK_SEARCH_V2_ITEM[] = [];

    if (!quickAddItems || !quickAddItems.length) {
      return simulationItems;
    }

    quickAddItems.forEach(item => {
      let newItem: QUICK_SEARCH_V2_ITEM | null = null;

      if (item.uiType === QuickAddUIType.GICSimulator) {
        newItem = {
          code: null,
          id: (item as QuickAddGICSimulator).key,
          lineItemAddLabel: formatMessage(`${i18nBasePath}.AddSimulation`),
          name: formatMessage(`${i18nBasePath}.GICSimulator`),
          pillLabel: (item as QuickAddGICSimulator).key,
        } as QUICK_SEARCH_V2_ITEM;
      } else if (item.uiType === QuickAddUIType.HISASimulator) {
        newItem = {
          code: null,
          id: (item as QuickAddHISASimulator).key,
          lineItemAddLabel: formatMessage(`${i18nBasePath}.AddSimulation`),
          name: formatMessage(`${i18nBasePath}.HISASimulator`),
          pillLabel: (item as QuickAddHISASimulator).key,
        } as QUICK_SEARCH_V2_ITEM;
      }

      if (newItem) {
        simulationItems.push(newItem);
      }
    });

    return simulationItems;
  };

  const getTabsOptions = () => {
    const benchmarksLabel = isComparisonReducer ? `${i18nBasePath}.Filters.Benchmarks` : '';
    const proxiesLabel = !isComparisonReducer ? `${i18nBasePath}.Filters.BenchmarkProxies` : '';
    const tabs: { [key in QUICK_SEARCH_FILTERS]: string } = {
      [QUICK_SEARCH_FILTERS.ALL]: `${i18nBasePath}.Filters.All`,
      [QUICK_SEARCH_FILTERS.FUNDS]: `${i18nBasePath}.Filters.Funds`,
      [QUICK_SEARCH_FILTERS.STOCKS]: `${i18nBasePath}.Filters.Stocks`,
      [QUICK_SEARCH_FILTERS.BONDS]: `${i18nBasePath}.Filters.Bonds`,
      [QUICK_SEARCH_FILTERS.SMA]: `${i18nBasePath}.Filters.SMA`,
      [QUICK_SEARCH_FILTERS.BENCHMARK_PROXIES]: proxiesLabel,
      [QUICK_SEARCH_FILTERS.BENCHMARKS]: benchmarksLabel,
      [QUICK_SEARCH_FILTERS.ALTS]: `${i18nBasePath}.Filters.Alts`,
      [QUICK_SEARCH_FILTERS.SIMULATION]: `${i18nBasePath}.Filters.Simulation`,
      [QUICK_SEARCH_FILTERS.PROXIES]: `${i18nBasePath}.Filters.Proxies`,
      [QUICK_SEARCH_FILTERS.FAVORITE]: `${i18nBasePath}.Filters.Favorite`,
    };
    return Object.keys(tabs)
      .map(key => ({
        value: key as QUICK_SEARCH_FILTERS,
        i18nKey: tabs[key as QUICK_SEARCH_FILTERS],
        hidden: !filterIsShown(key as QUICK_SEARCH_FILTERS, user, fundFilters, targetReducer, hiddenTabs),
        disabled: enableOnlyProxies && key !== QUICK_SEARCH_FILTERS.PROXIES,
      }))
      .filter(i => !i.hidden);
  };

  const handleAddedSearchItem = (id: string, type: PortfolioHoldingType | ComparisonItemType) => {
    const logDestination = isComparisonReducer ? SEARCH_ITEM_DEST.COMPARISON : SEARCH_ITEM_DEST.PORTFOLIO;

    addSearchItemLog(id, type, SEARCH_ITEM_SOURCE.QUICKSEARCH, logDestination);
    handleOnReplacingItem(true, true);
    handleRemoveFoundItem(id);

    if (isComparisonReducer || isEditBenchmarkReducer) {
      closeResults();
    }
  };

  const handleErrorOnAddItem = (id: string, error: Error) => {
    handleRemoveFoundItem(id);
    showError(error);
  };

  const handleGetResults = (filter: QUICK_SEARCH_FILTERS, query: string) => {
    debouncedFetchQuickSearchResults.cancel();

    if (!query) {
      return;
    }

    if (!isLoading) {
      setIsLoading(true);
    }

    // the favorite filter only filters the saved items by the user
    if (filter === QUICK_SEARCH_FILTERS.FAVORITE) {
      const items = getFavoriteItems();

      if (query) {
        const filteredItems = searchObjectsOnFields(query, items, ['symbol', 'name'], false);
        setFavoriteItems(filteredItems);
      } else {
        setFavoriteItems(items);
      }

      setIsLoading(false);
      return;
    }

    if (filter === QUICK_SEARCH_FILTERS.PROXIES) {
      const { items, isGrouped } = getProxyItems();
      const isFuzzySearch = userHasPermissionEnabled(user, PERMISSIONS.FUZZY_SEARCH);
      let fitleredProxyItems = items;
      if (query) {
        if (isGrouped) {
          const groupedItems = items as Record<string, QUICK_SEARCH_V2_ITEM[]>;
          fitleredProxyItems = filterGroupedProxyItems(groupedItems, query, isFuzzySearch);
        } else {
          const flatItems = items as QUICK_SEARCH_V2_ITEM[];
          fitleredProxyItems = searchObjectsOnFields(query, flatItems, ['name'], isFuzzySearch);
        }
      }
      updateProxyItems(fitleredProxyItems, isGrouped);
      setIsLoading(false);
      return;
    }

    // The simulation filter only filters the items type GIC or HISA
    if (filter === QUICK_SEARCH_FILTERS.SIMULATION) {
      const items = getSimulationItems();

      if (query) {
        const filteredItems = searchObjectsOnFields(query, items, ['pillLabel', 'name'], false);
        setSimulationItems(filteredItems);
      } else {
        setSimulationItems(items);
      }

      setIsLoading(false);
      return;
    }

    debouncedFetchQuickSearchResults(filter, query);
  };

  const handleOnReplacingItem = (clearHolding = true, clearResults = false) => {
    if (holdingToReplace && clearHolding) {
      setHoldingToReplace(undefined);
    }

    if (holdingToReplace && clearResults) {
      closeResults();
    }

    if (holdingToReplace) {
      searchInputRef.current?.blurInput();
      onSetFocusedItem(false);
    }
  };

  const handleRemoveFoundItem = (id: string) => {
    const otherItems = addingItemsIds.filter(aId => aId !== id);
    setAddingItemsIds(otherItems);
  };

  const onAddItem = (item: QUICK_SEARCH_V2_ITEM) => {
    const { id, type, proxyType } = item;

    if (!item || !id) {
      return;
    }

    // Collecting the ID of the item that needs to be added
    setAddingItemsIds(prevIds => Array.from(new Set([...prevIds, id])));

    if (isPortfolioReducer && proxyType === CUSTOM_PROXY_TYPE.SIMULATION) {
      setProxySimulationItem(item);
      setShowRateOfReturnModal(true);
      closeResults();
      return;
    }

    // Simulation GIC modal is needed only for portfolio
    if (isPortfolioReducer && id === YieldParamsType.GIC) {
      setShowGicModal(true);
      closeResults();
      return;
    }

    // Simulation HISA modal is needed only for portfolio
    if (isPortfolioReducer && id === YieldParamsType.HISA) {
      setShowHisaModal(true);
      closeResults();
      return;
    }

    switch (type) {
      case COMPARISON_ITEM_TYPES.ALT: {
        return handleAddItem(async () => onAddAltById(id, holdingToReplace, true), id, type);
      }
      case COMPARISON_ITEM_TYPES.BENCHMARK: {
        return handleAddItem(async () => onAddBenchmarkById(id, holdingToReplace, true), id, type);
      }
      case COMPARISON_ITEM_TYPES.FUND: {
        return handleAddItem(async () => onAddFundById(id, holdingToReplace, true), id, type);
      }
      case COMPARISON_ITEM_TYPES.STOCK: {
        return handleAddItem(async () => onAddStockById(id, holdingToReplace, true), id, type);
      }
      default: {
        return Promise.reject(new Error('Unsupported search item type'));
      }
    }
  };

  const handleAddItem = async (action: () => Promise<void>, id: string, type: PortfolioHoldingType | ComparisonItemType) => {
    try {
      await action();
      handleAddedSearchItem(id, type);
    } catch (e) {
      handleErrorOnAddItem(id, e as Error);
    }
  };

  const onChangeFilter = (filter: QUICK_SEARCH_FILTERS) => {
    if (selectedFilter === filter) {
      return;
    }

    setSelectedFilter(filter);

    if (!searchTerm && filter === QUICK_SEARCH_FILTERS.FAVORITE) {
      setFavoriteItems(getFavoriteItems());
    }

    if (!searchTerm && filter === QUICK_SEARCH_FILTERS.PROXIES) {
      const { items, isGrouped } = getProxyItems();
      updateProxyItems(items, isGrouped);
    }

    if (!searchTerm && filter === QUICK_SEARCH_FILTERS.SIMULATION) {
      setSimulationItems(getSimulationItems());
    }

    handleGetResults(filter, searchTerm);
  };

  const onSearch = (query: string) => {
    if (!query) {
      return clearSearch();
    }

    setSearchTerm(query);
    handleGetResults(selectedFilter, query);
  };

  const openScreenerModal = () => {
    onOpenScreenerModal(true);
    setShowResults(false);
  };

  const renderAllSections = () => {
    // Currently, there is no support for collapsible sections in the comparison quick-search.
    if (isComparisonReducer) return <></>;

    const allSections = getAllSections().reduce((acc: ALL_SECTION[], cur: ALL_SECTION) => {
      const curOptions = cur.getOptions();
      if (cur && curOptions.length) acc.push(cur);
      return acc;
    }, []);

    if (isEmpty(allSections)) {
      return (
        <div className="no-results">
          <div className="no-results-message" data-testid="no-search-history">
            <FormattedMessage id={`${i18nBasePath}.EmptyResults.NoSearchHistory`} tagName="h3" />
            <FormattedMessage id={`${i18nBasePath}.EmptyResults.BeginYourSearch`} tagName="p" />
          </div>
        </div>
      );
    }

    return allSections.map((section: ALL_SECTION) => (
      <div key={section.value} data-testid={`all-section-${section.value}`}>
        <CollapsibleContainer
          defaultOpen
          headerText={formatMessage(section.i18nKey)}
          icon="arrow-drop-down"
          // Force safari to give button focus for blur event to work properly
          tabIndex={0}
        >
          {section.getOptions().map(item => renderItem(item, section.value))}
        </CollapsibleContainer>
      </div>
    ));
  };

  const renderEmptyResults = () => {
    if (isLoading) {
      return null;
    }

    const isFavoriteFilter = selectedFilter === QUICK_SEARCH_FILTERS.FAVORITE;
    const isSimulationFilter = selectedFilter === QUICK_SEARCH_FILTERS.SIMULATION;
    const isProxyFilter = selectedFilter === QUICK_SEARCH_FILTERS.PROXIES;

    const isFavoriteEmpty = isFavoriteFilter && !favoriteItems.length;
    const isSimulationEmpty = isSimulationFilter && !simulationItems.length;
    const isProxiesEmpty = isProxyFilter && !getCustomProxiesCount(proxyItems);
    const isResultsEmpty = !results.length && !isFavoriteFilter && !isSimulationFilter && !isProxyFilter;

    const showNoFavoriteAdded = !searchTerm && isFavoriteEmpty;
    const showNoResultsFound = searchTerm && (isFavoriteEmpty || isSimulationEmpty || isResultsEmpty || isProxiesEmpty);
    const isItemSearchFilter = allSearchableOptions.includes(selectedFilter) || (selectedFilter === QUICK_SEARCH_FILTERS.ALL && isComparisonReducer);
    const showNoSearchHistory = !searchTerm && isItemSearchFilter;

    if (showNoResultsFound) {
      return (
        <div className="no-results-message" data-testid="no-results-found">
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NoResultsFound`} tagName="h3" />
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NotMatch`} tagName="p" values={{ searchTerm }} />
          <SecondaryLink
            className="secondary-link-v2"
            // Force safari to give button focus for blur event to work properly
            tabIndex={0}
            onClick={() => clearSearch(true)}
          >
            <FormattedMessage id={`${i18nBasePath}.EmptyResults.ClearSearch`} />
          </SecondaryLink>
        </div>
      );
    }

    if (showNoFavoriteAdded) {
      return (
        <div className="no-results-message" data-testid="no-favorite-added">
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NoFavoriteAdded`} tagName="h3" />
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.SearchOtherTabs`} tagName="p" />
        </div>
      );
    }

    const showNoProxiesAdded = !searchTerm && isProxyFilter && !getCustomProxiesCount(proxyItems);

    if (showNoProxiesAdded) {
      return (
        <div className="no-results-message" data-testid="no-proxies-added">
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NoProxiesAdded`} tagName="h3" />
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NoProxiesAddedDescription`} tagName="p" />
        </div>
      );
    }

    if (showNoSearchHistory) {
      return (
        <div className="no-results-message" data-testid="no-search-history">
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.NoSearchHistory`} tagName="h3" />
          <FormattedMessage id={`${i18nBasePath}.EmptyResults.BeginYourSearch`} tagName="p" />
        </div>
      );
    }

    return null;
  };

  const renderFavoriteResults = () => favoriteItems.map(item => renderItem(formatToQuickSearchItem(item), QUICK_SEARCH_FILTERS.FAVORITE));

  const renderProxyResults = () => {
    if (isProxyDataGrouped) {
      const groupedItems = proxyItems as Record<string, QUICK_SEARCH_V2_ITEM[]>;
      const groupedKeys = Object.keys(groupedItems);

      if (groupedKeys.length === 0) {
        return null;
      }

      return groupedKeys.map(sectionKey => (
        <div key={sectionKey} data-testid={`proxy-section-${sectionKey}`}>
          <CollapsibleContainer defaultOpen headerText={sectionKey} icon="arrow-drop-down">
            {groupedItems[sectionKey].map((item: QUICK_SEARCH_V2_ITEM) => renderItem(item, `${QUICK_SEARCH_FILTERS.PROXIES}-${sectionKey}`))}
          </CollapsibleContainer>
        </div>
      ));
    }
    const flatItems = proxyItems as QUICK_SEARCH_V2_ITEM[];
    return flatItems.map((item: QUICK_SEARCH_V2_ITEM) => renderItem(item, QUICK_SEARCH_FILTERS.PROXIES));
  };

  const renderItem = (item: QUICK_SEARCH_V2_ITEM, key: string) => {
    const { id } = item;
    const isAddedItem = validateIfItemIsAdded(id);
    const isAddingItem = !isAddedItem && addingItemsIds.includes(id);
    const isSingleAdd = !isPortfolioReducer;

    return (
      <QuickSearchV2Item
        isAddedItem={isAddedItem}
        isAddingItem={isAddingItem}
        isSingleAdd={isSingleAdd}
        item={item}
        key={`${key}-${id}`}
        onAddItem={onAddItem}
      />
    );
  };

  const renderReplaceProductTooltip = (name: LocalizedString) => (
    <div className="replace-tooltip">
      <Tooltip
        className="custom-tooltip-v2"
        title={formatMessage(`${i18nBasePath}.ReplaceTooltip.Title`)}
        message={formatMessage(`${i18nBasePath}.ReplaceTooltip.Message`, {
          product: helpers.getTrimmedHoldingName(formatLocalizedString(name)),
        })}
        place="left"
      >
        <span />
      </Tooltip>
    </div>
  );

  const renderSimulationResults = () => simulationItems.map(item => renderItem(item, QUICK_SEARCH_FILTERS.SIMULATION));

  const renderOpenScreener = () => (
    <>
      <FormattedMessage id={`${i18nBasePath}.CanNotFind`} />
      <SecondaryLink
        className="secondary-link-v2"
        // Force safari to give button focus for blur event to work properly
        tabIndex={0}
        onClick={openScreenerModal}
      >
        <i className="icon-expand-top-right" />
        <FormattedMessage id={`${i18nBasePath}.OpenScreener`} />
      </SecondaryLink>
    </>
  );

  const renderResults = () => {
    let content;

    // Currently, there is no support for collapsible sections in the comparison all search results.
    if (!isComparisonReducer && !searchTerm && selectedFilter === QUICK_SEARCH_FILTERS.ALL) {
      content = renderAllSections();
    } else if (
      searchTerm &&
      selectedFilter !== QUICK_SEARCH_FILTERS.FAVORITE &&
      selectedFilter !== QUICK_SEARCH_FILTERS.PROXIES &&
      selectedFilter !== QUICK_SEARCH_FILTERS.SIMULATION
    ) {
      content = renderSearchResults();
    } else if (selectedFilter === QUICK_SEARCH_FILTERS.FAVORITE) {
      content = renderFavoriteResults();
    } else if (selectedFilter === QUICK_SEARCH_FILTERS.PROXIES) {
      content = renderProxyResults();
    } else if (selectedFilter === QUICK_SEARCH_FILTERS.SIMULATION) {
      content = renderSimulationResults();
    } else {
      content = <></>;
    }

    return (
      <div role="listbox" aria-labelledby={`tab-${selectedFilter}-${componentId}`}>
        {content}
      </div>
    );
  };

  const renderResultsCounter = (filter: QUICK_SEARCH_FILTERS) => {
    const isFavoriteFilter = selectedFilter === QUICK_SEARCH_FILTERS.FAVORITE;
    const isFavoriteSearchEmpty = !searchTerm && isFavoriteFilter;
    const isProxiesFilter = selectedFilter === QUICK_SEARCH_FILTERS.PROXIES;
    const isProxiesSearchEmpty = !searchTerm && isProxiesFilter;
    const isSimulationFilter = selectedFilter === QUICK_SEARCH_FILTERS.SIMULATION;
    const isSimulationSearchEmpty = !searchTerm && isSimulationFilter;
    let counter: number | string = 0;

    // when the results are more than the resultsLimit (50), the counter should only display '50+'
    if (results.length >= resultsLimit) {
      counter = `${resultsLimit}+`;
    } else {
      counter = results.length;
    }

    if (isFavoriteFilter) {
      counter = favoriteItems.length;
    }

    if (isProxiesFilter) {
      counter = getCustomProxiesCount(proxyItems);
    }

    if (isSimulationFilter) {
      counter = simulationItems.length;
    }

    if (filter === selectedFilter && (searchTerm || isFavoriteSearchEmpty || isProxiesSearchEmpty || isSimulationSearchEmpty)) {
      return <span className="counter">{isLoading ? <LoadingSpinner /> : counter}</span>;
    }

    return null;
  };

  const renderSearchResults = () => {
    const favoriteItems = getFavoriteItems();
    const commonItems = favoriteItems.filter(fi => results.some(r => r._id === fi._id));
    const groupedResults = commonItems.concat(results.filter(r => !commonItems.some(si => si._id === r._id)));
    return groupedResults.map(item => renderItem(formatToQuickSearchItem(item), selectedFilter));
  };

  // this returns an object type QUICK_SEARCH_V2_ITEM to render the item
  // based on a item type of QuickSearchItem
  const formatToQuickSearchItem = (item: QuickSearchItem) => {
    const clonedQuickSearchItem = deepClone(item);
    const { _id, flags, name, saved, symbol, type } = clonedQuickSearchItem;
    const exchange: string | undefined = exchangeMapper(clonedQuickSearchItem);
    const code = exchange != null ? `${symbol}:${exchange}` : symbol;
    const shelfName = user ? getShelfNameFromShelvableInstrument(item, user) : undefined;

    return {
      code,
      flags,
      id: _id,
      isFavorite: saved,
      lineItemAddLabel: addItemDefaultLabel,
      name: formatLocalizedString(name),
      pillLabel: getPillLabel(item),
      type,
      shelfName,
    } as QUICK_SEARCH_V2_ITEM;
  };

  // this method formats the Cash & Proxies information to the Quick Add section
  // from redux we could receive items type: QuickAddItemsList | QuickAddGICSimulator | QuickAddHISASimulator
  const setQuickAddOptions = () => {
    const itemsTypeList: QuickAddItemsList[] = (quickAddItems?.filter(item => item.uiType === QuickAddUIType.ItemList).reverse() as QuickAddItemsList[]) ?? [];
    const cashItems: QUICK_SEARCH_V2_ITEM[] = [];
    const proxyItems: QUICK_SEARCH_V2_ITEM[] = [];

    // here we need to iterate only the filtered items with uiType 'ITEM_LIST'
    itemsTypeList.forEach((item: QuickAddItemsList) => {
      item.options.items?.forEach(optionsItem => {
        const { code, displayName, refId, type } = optionsItem;
        cashItems.push({
          code,
          id: refId,
          isProxy: true,
          lineItemAddLabel: addItemDefaultLabel,
          name: formatLocalizedString(displayName),
          pillLabel: INSTRUMENT_TYPE.CASH,
          type,
        });
      });

      item.options.subSections?.forEach(subSection => {
        subSection.items?.forEach(subSectionItem => {
          const { code, displayName, refId, type } = subSectionItem;
          proxyItems.push({
            code,
            id: refId,
            isProxy: true,
            lineItemAddLabel: addItemDefaultLabel,
            name: formatLocalizedString(displayName),
            pillLabel: INSTRUMENT_TYPE.PROXY,
            type,
          });
        });
      });
    });

    // we only return the Cash and Proxy items that were added in the itemsTypeList iteration
    return [...cashItems, ...proxyItems];
  };

  const setRecentSearchOptions = () =>
    // TODO: return the recentSearchItems when the endpoint will be ready
    // return recentSearchItems || [];
    [] as QUICK_SEARCH_V2_ITEM[];
  const validateIfItemIsAdded = (id: string) => {
    if (!id) return false;

    if (isComparisonReducer) {
      return !!comparison?.items?.some(item => item._id === id);
    }

    if (isPortfolioReducer) {
      return !!portfolio?.holdings?.some(holding => holding.original.id === id);
    }

    return false;
  };

  const handleCloseResults = () => {
    setShowResults(false);
    clearSearch();
    setSelectedFilter(QUICK_SEARCH_FILTERS.ALL);
  };

  const onBlurHandler = (event: React.FocusEvent) => {
    if (ignoreNextBlurRef.current) return;
    const { relatedTarget } = event;

    // Check if focus is leaving the entire component
    // Check relatedTarget is defined to support Safari's duplicate onBlur firing on forcibly focused buttons
    if (showResults && itemWrapperRef.current && relatedTarget && !itemWrapperRef.current.contains(relatedTarget as Node)) {
      handleCloseResults();
    }
  };

  // onPointerDownCapture and onPointerUpCapture to prevent us closing the panel when we temporarly loose focus by clicking on a panel elements like the scrollbar
  // FIXME : we should consider refactoring the close/open logic to avoid these workarounds
  function onPointerDownCapture() {
    ignoreNextBlurRef.current = true;
  }
  function onPointerUpCapture() {
    ignoreNextBlurRef.current = false;
  }

  const onKeyDownHandler = (event: React.KeyboardEvent) => {
    if (event.key === VALUE_ESCAPE && showResults) {
      event.preventDefault();
      handleCloseResults();
      searchInputRef.current?.blurInput();
    }
  };

  const handleRateOfReturnModalClose = () => {
    setShowRateOfReturnModal(false);
    setProxySimulationItem(null);
    handleOnReplacingItem();
  };

  const quickSearchResultsPanelId = `quick-search-results-${componentId}`;

  return (
    <>
      <div
        className={classNames('quick-search-v2', {
          'is-searching': showResults,
          'is-comparison': isComparisonReducer,
          'is-portfolio': isPortfolioReducer,
        })}
        ref={itemWrapperRef}
        onBlur={onBlurHandler}
        onPointerDownCapture={onPointerDownCapture}
        onPointerUpCapture={onPointerUpCapture}
        onKeyDown={onKeyDownHandler}
      >
        {holdingToReplace && showResults && renderReplaceProductTooltip(holdingToReplace.name)}

        <SearchInput
          className="search-input-v2"
          onChange={onSearch}
          onFocus={() => setShowResults(true)}
          onBlur={() => !showResults && handleOnReplacingItem(false)}
          placeholder={getSearchPlaceholder()}
          ref={searchInputRef}
          rightSection={searchTerm ? <i className="icon-cross-thin clear-term" onClick={() => clearSearch()} /> : undefined}
          value={searchTerm}
          role="combobox"
          aria-controls={quickSearchResultsPanelId}
          aria-expanded={showResults}
          aria-autocomplete="list"
          disabled={disabled}
        />

        {showResults && (
          <div
            className="results"
            data-testid="results"
            aria-label={formatMessage(`${i18nBasePath}.Accessibility.SearchResults`)}
            role="dialog"
            id={quickSearchResultsPanelId}
          >
            {allSearchableOptions.length > 1 && (
              <div className="tabs-wrapper">
                <PerfectScrollbar
                  options={{
                    suppressScrollY: true,
                    suppressScrollX: false,
                    wheelSpeed: 0.5,
                    minScrollbarLength: 20,
                  }}
                  className="tabs-scrollbar"
                >
                  <Tabs className="tabs-v2" onChange={onChangeFilter} selected={selectedFilter} role="tablist">
                    {getTabsOptions().map(({ value, i18nKey, disabled }) => (
                      <ControlItem
                        id={`tab-${value}-${componentId}`}
                        data-testid={`filter-${value}`}
                        key={value}
                        value={value}
                        disabled={disabled}
                        aria-selected={value === selectedFilter}
                        role="tab"
                        aria-controls={`tabpanel-${value}-${componentId}`}
                        // Force safari to give button focus for blur event to work properly
                        tabIndex={0}
                      >
                        <FormattedMessage id={i18nKey} />
                        {renderResultsCounter(value)}
                      </ControlItem>
                    ))}
                  </Tabs>
                </PerfectScrollbar>
              </div>
            )}

            <div role="tabpanel" id={`tabpanel-${selectedFilter}-${componentId}`} aria-labelledby={`tab-${selectedFilter}-${componentId}`}>
              <div className="results-list">{isLoading ? <LoadingSpinner /> : <PerfectScrollbar>{renderResults()}</PerfectScrollbar>}</div>

              <div className="no-results">{renderEmptyResults()}</div>
            </div>
            {withAdvancedScreener && !enableOnlyProxies && <div className="results-open-screener">{renderOpenScreener()}</div>}
          </div>
        )}
      </div>

      {showGicModal && (
        <SimulatorModal
          onCancel={() => {
            setShowGicModal(false);
            handleOnReplacingItem();
          }}
          onConfirm={() => setShowGicModal(false)}
          show={showGicModal}
          simulatorTitle={formatMessage(`${i18nBasePath}.GICSimulator`)}
          simulatorType={YieldParamsType.GIC}
        />
      )}

      {showHisaModal && (
        <SimulatorModal
          onCancel={() => {
            setShowHisaModal(false);
            handleOnReplacingItem();
          }}
          onConfirm={() => setShowHisaModal(false)}
          show={showHisaModal}
          simulatorTitle={formatMessage(`${i18nBasePath}.HISASimulator`)}
          simulatorType={YieldParamsType.HISA}
        />
      )}

      {showRateOfReturnModal && proxySimulationItem && (
        <RateOfReturnModal
          show={showRateOfReturnModal}
          filteredShelves={proxySimulationItem.filteredShelves}
          proxyName={proxySimulationItem.name}
          options={proxySimulationItem.options!}
          holdingToReplace={holdingToReplace}
          onClosed={handleRateOfReturnModalClose}
        />
      )}
    </>
  );
};

const mapTargetReducer = (state: ReduxState, type: string) => {
  if (type === QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO) {
    return state.quickSearchEditPortfolio;
  }
  if (type === QUICK_SEARCH_REDUCER_NAME.EDIT_BENCHMARK) {
    return state.quickSearchEditBenchmark;
  }
  return state.quickSearch;
};

const mapStateToProps = (state: ReduxState, ownProps: LocalProps) => {
  const targetReducer = mapTargetReducer(state, ownProps.targetReducer);
  return {
    fundState: state.advancedSearch.fund,
    isFocusedQuickSearch: targetReducer.focused,
    enableOnlyProxies: targetReducer.enableOnlyProxies,
    myBenchmarks: state.comparisonBenchmarks.savedBenchmarks.benchmarks,
    customProxies: state.customProxies.customProxies,
    myFunds: selectSavedFundsShelfStatusedFund(state.funds.savedFunds).funds,
    results: selectQuickSearchShelfStatusedResults(targetReducer).results,
    user: state.auth.user,
  };
};

const mapDispatchToProps = {
  addSearchItemLog,
  fetchQuickSearchResults,
  loadSearchFilters,
  setHoldingToReplace,
  getCustomProxies,
};

const ConnectedQuickSearchV2 = connect(mapStateToProps, mapDispatchToProps)(withErrorBoundary(QuickSearchV2));

// Type-safe conversion for withLegacyVersion compatibility
// Both components share the same core functionality and prop interface,
// with the new component having additional optional props that the legacy component ignores
type ConnectedComponentProps = React.ComponentProps<typeof ConnectedQuickSearchV2>;
const TypedLegacyComponent = LegacyQuickSearchV2 as React.ComponentType<ConnectedComponentProps>;

export default withLegacyVersion(ConnectedQuickSearchV2, TypedLegacyComponent);
